import {
  ChevronLeft,
  ChevronRight,
  Circle,
  FileText,
  Gauge,
  Mic,
  Pause,
  PauseCircle,
  Play,
  Repeat,
  Repeat1,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { EpisodeList } from '@/api/type'
import { SpeedControl } from '@/components/shared/speed-control'
import { Button } from '@/components/ui/button'
import { getEpisodeList } from '@/lib/db'
import { formatDuration } from '@/lib/time-format'
import { PLAYBACK_MODES, PlaybackMode, SHOW_TEXT_MODES, ShowTextMode } from '@/types/playback'

import { AutoPauseControl } from '../auto-pause-control'
import { ControlButton } from './control-button'
import { SkipButton } from './skip-button'

type PlaybackControlsProps = {
  isPlaying: boolean
  playbackSpeed: number
  currentTime: number
  duration: number
  onSeek: (time: number) => void
  onTogglePlay: () => void
  onSkipTime: (seconds: number) => void
  onChangeSpeed: (speed: number) => void
  autoPauseEnabled: boolean
  playbackMode: PlaybackMode
  onPlaybackModeChange: (mode: PlaybackMode) => void
  autoPauseMultiplier: number
  isPausedByAuto: boolean
  autoPauseProgress: number
  autoPauseTotal: number
  onAutoPauseEnabledChange: (enabled: boolean) => void
  onAutoPauseMultiplierChange: (multiplier: number) => void
  showText: ShowTextMode
  onShowTextChange: (showText: ShowTextMode) => void
  onRecordClick: () => void
  domain: string
  productId: string
  episodeId: string
  isRecording?: boolean
  recordingTime?: number
}

const SPEED_PRESETS = [0.5, 1.0, 1.2, 1.5, 2.0, 2.5, 3.0]
const formatSeekTime = (seconds: number) => {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

export const PlaybackControls = ({
  isPlaying,
  playbackSpeed,
  currentTime,
  duration,
  onSeek,
  onTogglePlay,
  onSkipTime,
  onChangeSpeed,
  autoPauseEnabled,
  autoPauseMultiplier,
  isPausedByAuto,
  autoPauseProgress,
  autoPauseTotal,
  onAutoPauseEnabledChange,
  onAutoPauseMultiplierChange,
  playbackMode,
  onPlaybackModeChange,
  showText,
  onShowTextChange,
  onRecordClick,
  domain,
  productId,
  episodeId,
  isRecording = false,
  recordingTime = 0,
}: PlaybackControlsProps) => {
  const navigate = useNavigate()
  const [showSpeedControl, setShowSpeedControl] = useState(false)
  const [showAutoPauseControl, setShowAutoPauseControl] = useState(false)
  const [episodeList, setEpisodeList] = useState<EpisodeList[]>([])

  const toggleSpeedControl = useCallback(() => {
    setShowSpeedControl((prev) => !prev)
  }, [])

  const toggleAutoPauseControl = useCallback(() => {
    setShowAutoPauseControl((prev) => !prev)
  }, [])

  const cyclePlaybackMode = useCallback(() => {
    if (playbackMode === PLAYBACK_MODES.ALL) {
      onPlaybackModeChange(PLAYBACK_MODES.SENTENCE)
    } else if (playbackMode === PLAYBACK_MODES.SENTENCE) {
      onPlaybackModeChange(PLAYBACK_MODES.ONCE)
    } else {
      onPlaybackModeChange(PLAYBACK_MODES.ALL)
    }
  }, [onPlaybackModeChange, playbackMode])

  const cycleShowText = useCallback(() => {
    if (showText === SHOW_TEXT_MODES.FULL) {
      onShowTextChange(SHOW_TEXT_MODES.HIDDEN)
    } else if (showText === SHOW_TEXT_MODES.HIDDEN) {
      onShowTextChange(SHOW_TEXT_MODES.TEXT_ONLY)
    } else {
      onShowTextChange(SHOW_TEXT_MODES.FULL)
    }
  }, [onShowTextChange, showText])

  const playbackModeLabel = useMemo(() => {
    switch (playbackMode) {
      case PLAYBACK_MODES.ALL:
        return 'truck'
      case PLAYBACK_MODES.SENTENCE:
        return 'sentence'
      case PLAYBACK_MODES.ONCE:
        return 'once'
      default:
        return 'once'
    }
  }, [playbackMode])

  const showTextLabel = useMemo(() => {
    switch (showText) {
      case SHOW_TEXT_MODES.FULL:
        return 'text'
      case SHOW_TEXT_MODES.HIDDEN:
        return 'time'
      case SHOW_TEXT_MODES.TEXT_ONLY:
        return 'reader'
    }
  }, [showText])

  // エピソードリストを取得
  useEffect(() => {
    const loadEpisodes = async () => {
      if (!domain || domain !== 'english' || !episodeId) {
        setEpisodeList([])
        return
      }

      try {
        const storedList = await getEpisodeList(domain)
        if (!storedList || !storedList.episodes) {
          setEpisodeList([])
          return
        }

        setEpisodeList(storedList.episodes)
      } catch (error) {
        console.error('Failed to load episode list:', error)
        setEpisodeList([])
      }
    }

    loadEpisodes()
  }, [domain, episodeId])

  // 前のエピソードを計算
  const [prevEpisode, nextEpisode] = useMemo(() => {
    if (!episodeList.length || !episodeId) return [null, null]

    const currentIndex = episodeList.findIndex((ep) => ep.domain_episode_id === episodeId)

    const prev = currentIndex <= 0 ? null : episodeList[currentIndex - 1]
    const next = currentIndex === -1 || currentIndex >= episodeList.length - 1 ? null : episodeList[currentIndex + 1]
    return [prev, next]
  }, [episodeList, episodeId])

  // 前のエピソードに移動
  const handlePrevEpisode = useCallback(() => {
    if (prevEpisode && domain && productId) {
      navigate(`/products/${domain}/${productId}/${prevEpisode.domain_episode_id}`)
    }
  }, [prevEpisode, domain, productId, navigate])

  // 次のエピソードに移動
  const handleNextEpisode = useCallback(() => {
    if (nextEpisode && domain && productId) {
      navigate(`/products/${domain}/${productId}/${nextEpisode.domain_episode_id}`)
    }
  }, [nextEpisode, domain, productId, navigate])

  const handleChangeSpeed = useCallback(
    (speed: number) => {
      onChangeSpeed(speed)
      setShowSpeedControl(false)
    },
    [onChangeSpeed],
  )

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-card border-t shadow-lg z-40 pt-4">
      {/* シークバー */}
      {duration > 0 && (
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs text-muted-foreground tabular-nums w-10 text-right">
            {formatSeekTime(currentTime)}
          </span>
          <input
            type="range"
            min={0}
            max={duration}
            step={0.1}
            value={currentTime}
            onChange={(e) => onSeek(Number(e.target.value))}
            className="flex-1 h-1.5 accent-primary cursor-pointer"
          />
          <span className="text-xs text-muted-foreground tabular-nums w-10">{formatSeekTime(duration)}</span>
        </div>
      )}
      <div className="p-4 pt-0">
        {showSpeedControl && (
          <SpeedControl
            presetSpeeds={SPEED_PRESETS}
            min={0.5}
            max={3.0}
            step={0.1}
            playbackSpeed={playbackSpeed}
            onChangeSpeed={handleChangeSpeed}
          />
        )}
        {/* 自動一時停止コントロール */}
        {showAutoPauseControl && (
          <AutoPauseControl
            autoPauseEnabled={autoPauseEnabled}
            autoPauseMultiplier={autoPauseMultiplier ?? 1.0}
            isPausedByAuto={isPausedByAuto ?? false}
            autoPauseProgress={autoPauseProgress ?? 0}
            autoPauseTotal={autoPauseTotal ?? 0}
            onAutoPauseEnabledChange={onAutoPauseEnabledChange}
            onAutoPauseMultiplierChange={onAutoPauseMultiplierChange}
          />
        )}

        {/* 再生速度ボタン */}
        <div className="flex items-center justify-center gap-2 mb-3">
          <ControlButton
            onClick={toggleSpeedControl}
            icon={<Gauge className="h-4 w-4" />}
            label={`${playbackSpeed.toFixed(1)}x`}
          />

          {/* 自動一時停止ボタン */}
          <ControlButton
            onClick={toggleAutoPauseControl}
            icon={<PauseCircle className="h-4 w-4" />}
            label={autoPauseEnabled ? `${autoPauseMultiplier.toFixed(1)}x` : 'OFF'}
          />

          {/* 連続再生ボタン */}
          <ControlButton
            onClick={cyclePlaybackMode}
            icon={
              playbackMode === PLAYBACK_MODES.ONCE ? <Repeat1 className="h-4 w-4" /> : <Repeat className="h-4 w-4" />
            }
            label={playbackModeLabel}
          />

          {/* テキスト表示ボタン */}
          <ControlButton onClick={cycleShowText} icon={<FileText className="h-4 w-4" />} label={showTextLabel} />

          {/* 録音ボタン */}
          <ControlButton
            onClick={onRecordClick}
            icon={
              isRecording ? (
                <Circle className="h-4 w-4 fill-red-500 text-red-500 animate-pulse" />
              ) : (
                <Mic className="h-4 w-4" />
              )
            }
            label={isRecording ? formatDuration(recordingTime / 1000) : 'record'}
          />
        </div>

        <div className="flex items-center justify-center flex-wrap mb-3">
          {/* 前のエピソードボタン */}
          <Button
            onClick={handlePrevEpisode}
            variant="ghost"
            className="relative w-14 h-14  [&_svg]:w-10! [&_svg]:h-10!"
            title={prevEpisode ? `前のエピソード: ${prevEpisode.domain_episode_id}` : '前のエピソードなし'}
            disabled={!prevEpisode}
            style={{ visibility: prevEpisode ? 'visible' : 'hidden' }}
          >
            <ChevronLeft size={32} />
          </Button>

          <SkipButton seconds={-10} onClick={() => onSkipTime(-10)} />
          <SkipButton seconds={-5} onClick={() => onSkipTime(-5)} />

          <Button onClick={onTogglePlay} variant="ghost" className="w-14 h-14 [&_svg]:w-10! [&_svg]:h-10!">
            {isPlaying ? <Pause size={32} /> : <Play size={32} />}
          </Button>

          <SkipButton seconds={5} onClick={() => onSkipTime(5)} />
          <SkipButton seconds={10} onClick={() => onSkipTime(10)} />

          {/* 次のエピソードボタン */}
          <Button
            onClick={handleNextEpisode}
            variant="ghost"
            className="relative w-14 h-14 [&_svg]:w-10! [&_svg]:h-10!"
            title={nextEpisode ? `次のエピソード: ${nextEpisode.domain_episode_id}` : '次のエピソードなし'}
            disabled={!nextEpisode}
            style={{ visibility: nextEpisode ? 'visible' : 'hidden' }}
          >
            <ChevronRight size={32} />
          </Button>
        </div>
      </div>
    </div>
  )
}
